import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useState, useEffect } from 'react';
import { useTheme, jsx } from '@emotion/react';
import '../../constants/breakpoints/index.js';
import '../../constants/grid/index.js';
import '../../constants/mediaQueries/index.js';
import '../../constants/boxShadows/primary.js';
import '../../constants/boxShadows/secondary.js';
import CheckmarkIcon from '../../icons/icons/checkmarkIcon.js';
import ChevronIcon from '../../icons/icons/chevronIcon.js';
import ErrorIcon from '../../icons/icons/errorIcon.js';
import InfoIcon from '../../icons/icons/infoIcon.js';
import { Avatar } from '../avatar/index.js';
import Style from './style.js';
import { isDefined } from '../../utils/isDefined/index.js';
import { useTruncateText } from './hooks.js';

var _excluded = ["avatarSrc", "content", "ctaContent", "duration", "onExit", "truncateText", "type"],
    _excluded2 = ["onExit"];
var ANIMATION_DELAY = 500;
var alertIconMapping = {
  success: CheckmarkIcon,
  error: ErrorIcon,
  danger: ErrorIcon,
  default: InfoIcon
};
/**
 * Alerts should be used to show notifications or messages from (providers, support, or system).
 *
 * They should show page-level success states.
 *
 * We should avoid using alerts to show flow-level success states (i.e. Cancellation successful)
 *
 * Alerts contain some visual marker, an icon `16px` or avatar `32px`
 *
 * Alerts should have a **bolded** portion of the text that helps inform the user what the alert is about.
 * The text should be short and preferably not extend more than 2 lines. Ellipses (...) can be used to truncate past 2 lines via the prop `truncateText`.
 *
 * Alerts can contain a CTA section. These should be reserved for really important actions. These alerts persists until an action is taken.
 *
 * The `<Alert />` component provides only the styling for and enter/exit behavior for alerts.
 * Management of what alerts are showing on the screen must be handled from outside of this component.
 *
 * All alerts are dimissable by clicking on them. However, you can use the `duration` prop to determine if the alert is sticky or dismissed on a timer (in units of seconds).
 */

var Alert = alertProps => {
  var {
    avatarSrc = '',
    content,
    ctaContent = null,
    duration = 3,
    onExit = () => undefined,
    truncateText = false,
    type = 'default'
  } = alertProps,
      rest = _objectWithoutProperties(alertProps, _excluded);

  var theme = useTheme();
  var [exiting, setExiting] = useState(false);
  var [exited, setExited] = useState(false);
  var {
    contentText
  } = useTruncateText(truncateText);
  var timer;

  var alertExitHandler = () => {
    setExiting(true);
    window.clearTimeout(timer);

    var otherProps = _objectWithoutProperties(alertProps, _excluded2);

    window.setTimeout(() => {
      setExited(true);
      onExit(otherProps);
    }, ANIMATION_DELAY);
  };
  /**
   * Duration logic effect
   */


  useEffect(() => {
    if (duration === 'sticky' || !!ctaContent) {
      return;
    }

    timer = window.setTimeout(alertExitHandler, Number(duration) * 1000 - ANIMATION_DELAY);
    return () => {
      if (isDefined(timer)) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  if (exited) {
    return null;
  }

  var Icon = alertIconMapping[type];
  return jsx(Style.AlertContainer, _extends({
    alertType: type,
    exiting: exiting,
    onClick: alertExitHandler // eslint-disable-next-line react/jsx-props-no-spreading

  }, rest), jsx(Style.MainContainer, null, jsx(Style.IconContainer, {
    hasAvatar: !!avatarSrc
  }, avatarSrc ? jsx(Avatar, {
    size: "small",
    src: avatarSrc,
    alt: "avatar"
  }) : jsx(Icon, {
    fill: theme.COLORS.white
  })), jsx(Style.ContentContainer, {
    truncateText: truncateText,
    ref: contentText
  }, content)), ctaContent && jsx(Style.CtaContent, null, jsx("div", null, ctaContent), jsx(ChevronIcon, {
    fill: theme.COLORS.white,
    width: 14,
    height: 14
  })));
};
Alert.Container = Style.AlertsContainer;

export { Alert };
